import { Divider, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { ExtendedSelectSearch } from "./extended-select-search"
import {
    ExtendedSelectBefore,
    ExtendedSelectErrorChecker,
    ExtendedSelectNoOptions,
    ExtendedSelectNoOptionsValue,
    ExtendedSelectRight,
    ExtendedSelectTop,
} from "slot-definitions"
import { setFromEvent } from "lib/setFromEvent"
import Iconify from "minimals-template/components/Iconify"
import { ExtendedSelectMenuItem } from "./extended-select-menu-item"
import { useExtraOptions } from "./hooks/use-extra-options"
import { useSelectFieldErrors } from "./hooks/use-select-field-errors"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { hasValue } from "lib/@components/extended-select/plugins/required-field-error/has-value"

/**
 * A customisable MUI select component that supports multiple selections, search functionality, and custom option
 * rendering.
 *
 * @param {Object} props - The props for the `ExtendedSelect` component.
 * @param {string} props.selectId - A unique identifier for the select input element.
 * @param {Array} props.options - The list of options to display in the select menu.
 * @param {string|string[]} props.value - The currently selected value(s). For multiple selections, this is an array.
 * @param {string} props.label - The label for the select input.
 * @param {boolean} [props.multiple=false] - Whether the select allows multiple selections.
 * @param {boolean} [props.required=false] - Whether the select is required.
 * @param {boolean} [props.search=false] - Whether the select allows searching of options.
 * @param {function} props.onSelect - A callback function that is triggered when an option is selected.
 * @param {function} props.onClear - A callback function that is triggered when the selected value is cleared.
 * @param {React.Component} [props.OptionItem=ExtendedSelectMenuItem] - A custom component used to render each option
 *     in the select menu.
 *
 * @returns {JSX.Element} The rendered select input component.
 */
export function ExtendedSelect({
    selectId,
    options,
    value,
    label,
    multiple = false,
    required = false,
    search = false,
    onSelect,
    onClear,
    OptionItem = ExtendedSelectMenuItem,
}) {
    const { mapping } = useBoundContext()
    const [selected, setSelected] = useState(value)
    const [open, setOpen] = useState(false)
    const [searchText, setSearchText] = useState("")
    const [sort, setSort] = useState(false)
    const [hover, setHover] = useState(false)

    const { hasFieldError } = useSelectFieldErrors(selectId)
    const { extraOptions, setExtraOptions } = useExtraOptions(selectId)

    const displayLabel = `${label}${required ? " *" : ""}`
    const labelId = `extended-select-label-${selectId}`
    const noOptionsError = options.length === 0 && required
    const hasSelectedValue = multiple ? selected.length > 0 : !!selected

    const filteredOptions = useMemo(() => {
        const filtered = options.filter(
            ({ name }) => typeof name === "string" && name?.toLowerCase()?.includes(searchText.toLowerCase())
        )
        return sort ? filtered.sortBy("name") : filtered
    }, [options, searchText, sort])

    useEffect(() => {
        setSelected(value)
    }, [value])

    if (!selectId) {
        console.error("Must provide an id property")
        return null
    }

    return (
        <Bound
            selectId={selectId}
            selectedOption={selected}
            options={options}
            extraOptions={extraOptions}
            multiple={multiple}
            required={required}
            hasSelectedValue={hasSelectedValue}
            label={label}
        >
            <ExtendedSelectErrorChecker.Slot />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 1 }}>
                <ExtendedSelectBefore.Slot type={selectId} />

                <FormControl fullWidth error={(noOptionsError || hasFieldError) && !hasValue(mapping)} size="small">
                    <InputLabel id={labelId} shrink={noOptionsError ? true : undefined}>
                        {displayLabel}
                    </InputLabel>
                    <Select
                        MenuProps={{ autoFocus: false }}
                        value={selected}
                        multiple={multiple}
                        labelId={labelId}
                        data-cy={`extended-select-${selectId}`}
                        label={displayLabel}
                        open={open}
                        size="small"
                        renderValue={renderSelectedValue}
                        displayEmpty={noOptionsError ? true : undefined}
                        onChange={setFromEvent(handleSelectChange)}
                        onOpen={handleOpenMenu}
                        onClose={handleCloseMenu}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        endAdornment={
                            hasSelectedValue &&
                            hover &&
                            !open && (
                                <InputAdornment sx={{ position: "absolute", right: 30 }} position="end">
                                    <IconButton
                                        aria-label="Clear selection"
                                        size="small"
                                        edge="end"
                                        onClick={handleClear}
                                    >
                                        <Iconify icon={"ic:round-clear"} />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                    >
                        {options.length === 0 ? (
                            <ExtendedSelectNoOptions.Slot>
                                <MenuItem value="">
                                    <em>No options</em>
                                </MenuItem>
                            </ExtendedSelectNoOptions.Slot>
                        ) : (
                            [
                                ...(search
                                    ? [
                                          <ExtendedSelectSearch
                                              key="search"
                                              setSearchText={setSearchText}
                                              setSort={setSort}
                                              sort={sort}
                                          />,
                                          <Divider key="divider" />,
                                      ]
                                    : []),
                                <ExtendedSelectTop.Slot
                                    key="extended-select-top-slot"
                                    closeMenu={handleCloseMenu}
                                    searchText={searchText}
                                    onSelect={handleSelectChange}
                                />,
                                filteredOptions.map((option, index) => (
                                    <OptionItem
                                        key={`${option.name}-${index}`}
                                        value={option.name}
                                        option={option.name}
                                        data-cy={`extended-select-option-${index}`}
                                    />
                                )),
                            ]
                        )}

                        <MenuItem key="extended-select-option-hidden" value={selected} style={{ display: "none" }}>
                            {selected}
                        </MenuItem>
                    </Select>
                </FormControl>
                <ExtendedSelectRight.Slot type={selectId} />
            </Stack>
        </Bound>
    )

    function handleOpenMenu() {
        setOpen(true)
    }

    function handleCloseMenu() {
        setOpen(false)
        setTimeout(() => setSearchText(""), 150)
    }

    function handleClear(event) {
        event.stopPropagation()
        setExtraOptions({})
        onClear()
    }

    function renderSelectedValue(selected) {
        if (noOptionsError)
            return <ExtendedSelectNoOptionsValue.Slot>Error - No Data</ExtendedSelectNoOptionsValue.Slot>
        return !multiple ? selected || "Select" : selected.join(", ")
    }

    function handleSelectChange(value) {
        const selectedValue = !multiple ? value : typeof value === "string" ? value.split(",") : value
        onSelect(selectedValue)
    }
}
